import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import { getConfig } from '@/config'
import { useCurrentUserStore } from '@/stores/currentUser.store'

export const useAuthStore = defineStore('auth', () => {
  const loading = ref(false)

  const currentUserStore = useCurrentUserStore()

  /** Authenticated when the current user is loaded (cookie is httpOnly, can't read it) */
  const isAuthenticated = computed(() => !!currentUserStore.authUser)

  /** Flat AuthUser for sidebar/navigation — delegates to currentUser store */
  const currentUser = computed(() => currentUserStore.authUser)

  /** ID of the user who started this impersonation session, or null */
  const impersonatedBy = computed(() => currentUserStore.impersonatedBy)

  async function fetchCurrentUser() {
    await currentUserStore.fetchCurrentUser()
  }

  /** Redirect to backend's Google OAuth login endpoint */
  function loginWithGoogle() {
    const apiBase = getConfig().apiBaseUrl
    window.location.href = `${apiBase}/auth/google/login`
  }

  /** Called from AuthCallbackView — cookie already set by backend redirect */
  async function handleCallback() {
    loading.value = true
    try {
      currentUserStore.user = null
      await currentUserStore.fetchCurrentUser()
    } finally {
      loading.value = false
    }
  }

  async function hijack(targetUserId: number) {
    await apiClient.post(`/auth-user/hijack/${targetUserId}`)
    currentUserStore.user = null
    await currentUserStore.fetchCurrentUser()
  }

  async function exitHijack() {
    await apiClient.post('/auth-user/hijack/exit')
    currentUserStore.user = null
    await currentUserStore.fetchCurrentUser()
  }

  async function logout() {
    try {
      await apiClient.post('/auth/logout')
    } finally {
      currentUserStore.user = null
    }
  }

  async function init() {
    try {
      await fetchCurrentUser()
    } catch {
      // No valid session — user stays null, will be redirected to login
    }
  }

  return {
    currentUser,
    impersonatedBy,
    loading,
    isAuthenticated,
    loginWithGoogle,
    handleCallback,
    fetchCurrentUser,
    hijack,
    exitHijack,
    logout,
    init,
  }
})
